import { useEffect, useState } from 'react';
import {
  THEME_CHANGE_EVENT,
  THEME_STORAGE_KEY,
  normalizeRawThemeStorageToken,
  normalizeThemeStorageValue,
} from '../../hooks/useTheme';

function getSafeLocalStorage() {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function parseStorageThemeSyncState(event, options = {}) {
  const eventRecord =
    event && (typeof event === 'object' || typeof event === 'function') ? event : {};

  if (!('key' in eventRecord)) {
    return undefined;
  }

  const isThemeStorageKey = eventRecord.key === THEME_STORAGE_KEY;
  const isClearStorageEvent = eventRecord.key === null;
  const isSupportedStorageKey = isThemeStorageKey || isClearStorageEvent;
  const hasStorageArea = 'storageArea' in eventRecord;
  const localStorageRef =
    Object.prototype.hasOwnProperty.call(options, 'localStorageRef')
      ? options.localStorageRef
      : getSafeLocalStorage();
  const isNullStorageArea = hasStorageArea && eventRecord.storageArea === null;
  const isInvalidStorageArea =
    hasStorageArea &&
    eventRecord.storageArea &&
    (!localStorageRef || eventRecord.storageArea !== localStorageRef);

  if (!isSupportedStorageKey || isNullStorageArea || isInvalidStorageArea) {
    return undefined;
  }

  if (isClearStorageEvent) {
    return eventRecord.newValue === null ? false : undefined;
  }

  const hasNewValue = 'newValue' in eventRecord;
  const hasUnsupportedNewValue =
    !hasNewValue || typeof eventRecord.newValue === 'undefined';
  const hasMalformedNewValueType =
    eventRecord.newValue !== null && typeof eventRecord.newValue !== 'string';

  if (hasUnsupportedNewValue || hasMalformedNewValueType) {
    return undefined;
  }

  const normalizedThemeValue = normalizeThemeStorageValue(eventRecord.newValue);
  const normalizedRawValue = normalizeRawThemeStorageToken(eventRecord.newValue);

  if (normalizedThemeValue === 'dark') {
    return true;
  }

  if (
    normalizedThemeValue === 'light' ||
    normalizedRawValue === 'null' ||
    eventRecord.newValue === null
  ) {
    return false;
  }

  return null;
}

export function useIsDarkTheme() {
  const [isDark, setIsDark] = useState(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return undefined;
    }

    const html = document.documentElement;
    const localStorageRef = getSafeLocalStorage();
    const syncIsDark = (nextIsDark) => {
      setIsDark((currentIsDark) =>
        currentIsDark === nextIsDark ? currentIsDark : nextIsDark
      );
    };

    const sync = (event) => {
      if (event?.type === 'storage') {
        const storageSyncState = parseStorageThemeSyncState(event, { localStorageRef });

        if (typeof storageSyncState === 'boolean') {
          syncIsDark(storageSyncState);
          return;
        }

        if (typeof storageSyncState === 'undefined') {
          return;
        }
      }

      if (
        event?.type === THEME_CHANGE_EVENT &&
        typeof event?.detail?.isDark === 'boolean'
      ) {
        syncIsDark(event.detail.isDark);
        return;
      }

      syncIsDark(html.classList.contains('dark'));
    };

    const observer =
      typeof MutationObserver === 'function' ? new MutationObserver(sync) : null;
    observer?.observe(html, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    window.addEventListener('pageshow', sync);
    window.addEventListener(THEME_CHANGE_EVENT, sync);
    document.addEventListener('visibilitychange', sync);

    return () => {
      observer?.disconnect();
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
      window.removeEventListener('pageshow', sync);
      window.removeEventListener(THEME_CHANGE_EVENT, sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  return isDark;
}
